import React, { useState, useId } from "react";
import { Check } from "lucide-react";

/**
 * Inline editor for ONE existing extra service (IGFEMS-595). Rendered by
 * ExtraServicesEditor in place of the read-only row. Owns its draft state; calls
 * onSave with the changed fields only, so the parent can keep any other keys on the
 * service (e.g. date_keys) untouched.
 *
 * A native datalist only lists options matching the text already in the input, so the
 * current name would hide every other suggestion. While the field is focused and
 * untouched it shows empty (current name as placeholder) so all suggestions are offered.
 */
const ExtraServiceEditRow = ({ service, suggestions = [], onSave, onCancel }) => {
  const datalistId = useId();
  const [name, setName] = useState(service.service_name || "");
  const [replacing, setReplacing] = useState(false);
  const [price, setPrice] = useState(String(service.price ?? ""));
  const [allocationType, setAllocationType] = useState(
    service.allocation_type === "private" ? "private" : "required"
  );

  const isValid =
    name.trim() && price !== "" && !Number.isNaN(Number(price)) && Number(price) >= 0;

  const save = () => {
    if (!isValid) return;
    onSave({ service_name: name.trim(), price: Number(price), allocation_type: allocationType });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="extra-services-editor__row extra-services-editor__row--draft extra-services-editor__row--edit">
      <div className="extra-services-editor__field">
        <input
          type="text"
          className="form-control"
          list={datalistId}
          value={replacing ? "" : name}
          onFocus={() => setReplacing(true)}
          onBlur={() => setReplacing(false)}
          onChange={(e) => {
            setReplacing(false);
            setName(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={replacing ? name : "Service name"}
          aria-label="Service name"
          autoFocus
        />
        <datalist id={datalistId}>
          {suggestions.map((s) => (
            <option value={s} key={s} />
          ))}
        </datalist>
      </div>

      <div className="extra-services-editor__field">
        <select
          className="form-select"
          value={allocationType}
          onChange={(e) => setAllocationType(e.target.value)}
          aria-label="Service type"
        >
          <option value="required">Required</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div className="extra-services-editor__field extra-services-editor__price-field">
        <span className="extra-services-editor__price-prefix">$</span>
        <input
          type="number"
          min={0}
          className="form-control"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="0.00"
          aria-label="Service price"
        />
      </div>

      <div className="extra-services-editor__edit-actions">
        <button
          type="button"
          className="btn btn-warning btn-sm extra-services-editor__save-btn"
          aria-label="Save"
          title="Save"
          disabled={!isValid}
          onClick={save}
        >
          <Check size={18} aria-hidden="true" />
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ExtraServiceEditRow;
